import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AccessDeniedPage } from '@/app/pages/AccessDeniedPage';
import { useAccessBlockedStore, useAuthStore } from '@/features/auth';
import { ROUTES } from '@/constants';
import { queryClient } from '@/lib/queryClient';

interface RequireAuthProps {
	children: ReactNode;
}

/**
 * Route guard for authenticated areas. Redirects unauthenticated users to
 * the login page while remembering where they were headed, and shows the
 * access-denied page while the workspace's IP allowlist refuses the network.
 */
export function RequireAuth({ children }: RequireAuthProps) {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const clearSession = useAuthStore((state) => state.clearSession);
	const blocked = useAccessBlockedStore((state) => state.error);
	const resetBlocked = useAccessBlockedStore((state) => state.reset);
	const location = useLocation();

	if (!isAuthenticated) {
		return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
	}

	if (blocked) {
		return (
			<AccessDeniedPage
				{...(blocked.requestId ? { requestId: blocked.requestId } : {})}
				onRetry={() => {
					resetBlocked();
					// Failed queries refetch on remount; refresh the rest too.
					void queryClient.invalidateQueries();
				}}
				onSignOut={clearSession}
			/>
		);
	}

	return children;
}
